import React, { useEffect } from 'react';

import { Typography } from '@mui/material';
import dayjs from 'dayjs';
import { doc, getDoc } from 'firebase/firestore';
import { t } from 'i18next';
import InfiniteScroll from 'react-infinite-scroller';

import { companiesRef } from '../../api/firebase/firestore/company';
import { getIssueInfiniteScrollfromClient } from '../../api/firebase/firestore/issue';
import { usersRef } from '../../api/firebase/firestore/user';
import { screenType } from '../../constants/enum';
import { AuthContext } from '../../contexts/auth';
import { throwError } from '../../modules/error';

import ClientAgentIssueSearchCard from './ClientAgentIssueSearchCard';
import CustomLoading from './CustomLoading';
import MypageIssueCard from './MypageIssueCard';
import NothingGivenList from './NothingGivenList';

import type { timestamp } from '../../api/firebase/firestore/issue';
import type { Issue } from '../../api/firebase/firestore/issue/issueType';
import type { Dayjs } from 'dayjs';
import type { DocumentData, QueryDocumentSnapshot } from 'firebase/firestore';

const styles = {
  card: {
    '@media screen and (max-width: 960px)': {
      margin: '16px'
    },
    margin: '24px'
  }
};

const ClientIssueList = (): JSX.Element => {
  const [issueList, setIssueList] = React.useState<Issue[]>([]);

  const [hasMore, setHasMore] = React.useState(true);

  const { currentUser } = React.useContext(AuthContext);
  const [loading, setLoading] = React.useState<boolean>(false);
  const [showList, setShowList] = React.useState<boolean>(false);

  const [searchedIssues, setSearchedIssues] = React.useState<Issue[]>([]);
  const [searchProductName, setSearchProductName] = React.useState('');
  const [searchStartDate, setSearchStartDate] = React.useState<{
    start: Dayjs | null;
    end: Dayjs | null;
  }>({ end: null, start: null });
  const [searchApplicationDate, setSearchApplicationDate] = React.useState<{
    start: Dayjs | null;
    end: Dayjs | null;
  }>({ end: null, start: null });
  const [searchGenre, setSearchGenre] = React.useState<string | number>(0);
  const [searchPurpose, setSearchPurpose] = React.useState<string | number>(0);
  const [searchBudget, setSearchBudget] = React.useState<
    string | number | null
  >(null);
  const [searchMediaType, setSearchMediaType] = React.useState<
    string | number | null
  >(null);
  const [searchMenu, setSearchMenu] = React.useState<number | null>(null);

  const handleSearchInputChange = (
    productName: string | null | undefined,
    startDate: { start: Dayjs | null; end: Dayjs | null },
    applicationDate: { start: Dayjs | null; end: Dayjs | null },
    genre: string | number | null | undefined,
    budget: string | number | null,
    purpose: string | number | null,
    mediaType: string | number | null,
    menu: number | null
  ): void => {
    if (productName) {
      setSearchProductName(productName);
    } else {
      setSearchProductName('');
    }

    if (budget) {
      setSearchBudget(budget);
    } else {
      setSearchBudget(null);
    }

    if (mediaType) {
      setSearchMediaType(mediaType);
    } else {
      setSearchMediaType(null);
    }

    if (menu) {
      setSearchMenu(menu);
    } else {
      setSearchMenu(null);
    }

    if (startDate.start && startDate.end) {
      setSearchStartDate({ end: startDate.end, start: startDate.start });
    } else if (startDate.start) {
      setSearchStartDate({ end: null, start: startDate.start });
    } else if (startDate.end) {
      setSearchStartDate({ end: startDate.end, start: null });
    } else {
      setSearchStartDate({ end: null, start: null });
    }

    if (applicationDate.start && applicationDate.end) {
      setSearchApplicationDate({
        end: applicationDate.end,
        start: applicationDate.start
      });
    } else if (applicationDate.start) {
      setSearchApplicationDate({ end: null, start: applicationDate.start });
    } else if (applicationDate.end) {
      setSearchApplicationDate({ end: applicationDate.end, start: null });
    } else {
      setSearchApplicationDate({ end: null, start: null });
    }

    if (genre) {
      setSearchGenre(genre);
    } else {
      setSearchGenre('');
    }

    if (purpose) {
      setSearchPurpose(purpose);
    } else {
      setSearchPurpose('');
    }
  };

  useEffect(() => {
    let filteredIssues = issueList;

    if (searchProductName) {
      filteredIssues = filteredIssues.filter((issue: Issue) =>
        issue?.productName?.includes(searchProductName)
      );
    }

    if (searchStartDate.start && searchStartDate.end) {
      filteredIssues = filteredIssues.filter(
        (value: DocumentData) =>
          searchStartDate.start &&
          searchStartDate.end &&
          dayjs
            .unix((value.startDate as timestamp)?.seconds)
            .format('YYYY/MM/DD') >=
            searchStartDate.start.format('YYYY/MM/DD') &&
          dayjs
            .unix((value.startDate as timestamp)?.seconds)
            .format('YYYY/MM/DD') <= searchStartDate.end.format('YYYY/MM/DD')
      );
    } else if (searchStartDate.start) {
      filteredIssues = filteredIssues.filter(
        (value: DocumentData) =>
          searchStartDate.start &&
          dayjs
            .unix((value.startDate as timestamp)?.seconds)
            .format('YYYY/MM/DD') >= searchStartDate.start.format('YYYY/MM/DD')
      );
    } else if (searchStartDate.end) {
      filteredIssues = filteredIssues.filter(
        (value: DocumentData) =>
          searchStartDate.end &&
          dayjs
            .unix((value.startDate as timestamp)?.seconds)
            .format('YYYY/MM/DD') <= searchStartDate.end.format('YYYY/MM/DD')
      );
    }

    if (searchApplicationDate.start && searchApplicationDate.end) {
      filteredIssues = filteredIssues.filter(
        (value: DocumentData) =>
          searchApplicationDate.start &&
          searchApplicationDate.end &&
          dayjs
            .unix((value.applicationDate as timestamp).start?.seconds)
            .format('YYYY/MM/DD') >=
            searchApplicationDate.start.format('YYYY/MM/DD') &&
          dayjs
            .unix((value.applicationDate as timestamp).end?.seconds)
            .format('YYYY/MM/DD') <=
            searchApplicationDate.end.format('YYYY/MM/DD')
      );
    } else if (searchApplicationDate.start) {
      filteredIssues = filteredIssues.filter(
        (value: DocumentData) =>
          searchApplicationDate.start &&
          dayjs
            .unix((value.applicationDate as timestamp).start?.seconds)
            .format('YYYY/MM/DD') >=
            searchApplicationDate.start.format('YYYY/MM/DD')
      );
    } else if (searchApplicationDate.end) {
      filteredIssues = filteredIssues.filter(
        (value: DocumentData) =>
          searchApplicationDate.end &&
          dayjs
            .unix((value.applicationDate as timestamp).end?.seconds)
            .format('YYYY/MM/DD') <=
            searchApplicationDate.end.format('YYYY/MM/DD')
      );
    }

    if (searchGenre) {
      filteredIssues = filteredIssues.filter(
        (issue: Issue) => issue?.genre === Number(searchGenre)
      );
    }

    if (searchPurpose) {
      filteredIssues = filteredIssues.filter(
        (issue: Issue) =>
          issue.purpose && issue.purpose[0] === Number(searchPurpose)
      );
    }

    if (searchBudget) {
      filteredIssues = filteredIssues.filter(
        (issue: Issue) => issue?.budget === searchBudget
      );
    }

    if (searchMediaType) {
      filteredIssues = filteredIssues.filter(
        (issue: Issue) =>
          issue.mediaType && issue?.mediaType[0] === searchMediaType
      );
    }

    if (searchMenu) {
      filteredIssues = filteredIssues.filter(
        (issue: Issue) =>
          Array.isArray(issue?.menu) &&
          (issue?.menu as number[]).includes(searchMenu)
      );
    }

    filteredIssues = filteredIssues.filter(
      (issue, index, self) =>
        index === self.findIndex((i) => i.issueId === issue.issueId)
    );

    setSearchedIssues(filteredIssues);
  }, [
    searchProductName,
    searchStartDate,
    searchApplicationDate,
    searchGenre,
    searchPurpose,
    searchBudget,
    searchMediaType,
    searchMenu
  ]);

  const appendIssues = (
    snapshot: Array<QueryDocumentSnapshot<DocumentData>>
  ): void => {
    const gotIssues: Issue[] = snapshot.map((document) => {
      const issue = document.data() as Issue;
      return issue;
    });

    setIssueList(issueList.concat(gotIssues));
    setSearchedIssues((prevIssues) => {
      let result = prevIssues.concat(gotIssues);
      if (searchProductName) {
        result = result.filter((issue: Issue) =>
          issue?.productName?.includes(searchProductName)
        );
      }

      if (searchStartDate.start && searchStartDate.end) {
        result = result.filter(
          (value: DocumentData) =>
            searchStartDate.start &&
            searchStartDate.end &&
            dayjs
              .unix((value.startDate as timestamp)?.seconds)
              .format('YYYY/MM/DD') >=
              searchStartDate.start.format('YYYY/MM/DD') &&
            dayjs
              .unix((value.startDate as timestamp)?.seconds)
              .format('YYYY/MM/DD') <= searchStartDate.end.format('YYYY/MM/DD')
        );
      } else if (searchStartDate.start) {
        result = result.filter(
          (value: DocumentData) =>
            searchStartDate.start &&
            dayjs
              .unix((value.startDate as timestamp)?.seconds)
              .format('YYYY/MM/DD') >=
              searchStartDate.start.format('YYYY/MM/DD')
        );
      } else if (searchStartDate.end) {
        result = result.filter(
          (value: DocumentData) =>
            searchStartDate.end &&
            dayjs
              .unix((value.startDate as timestamp)?.seconds)
              .format('YYYY/MM/DD') <= searchStartDate.end.format('YYYY/MM/DD')
        );
      }

      if (searchApplicationDate.start && searchApplicationDate.end) {
        result = result.filter(
          (value: DocumentData) =>
            searchApplicationDate.start &&
            searchApplicationDate.end &&
            dayjs
              .unix((value.applicationDate as timestamp).start?.seconds)
              .format('YYYY/MM/DD') >=
              searchApplicationDate.start.format('YYYY/MM/DD') &&
            dayjs
              .unix((value.applicationDate as timestamp).end?.seconds)
              .format('YYYY/MM/DD') <=
              searchApplicationDate.end.format('YYYY/MM/DD')
        );
      } else if (searchApplicationDate.start) {
        result = result.filter(
          (value: DocumentData) =>
            searchApplicationDate.start &&
            dayjs
              .unix((value.applicationDate as timestamp).start?.seconds)
              .format('YYYY/MM/DD') >=
              searchApplicationDate.start.format('YYYY/MM/DD')
        );
      } else if (searchApplicationDate.end) {
        result = result.filter(
          (value: DocumentData) =>
            searchApplicationDate.end &&
            dayjs
              .unix((value.applicationDate as timestamp).end?.seconds)
              .format('YYYY/MM/DD') <=
              searchApplicationDate.end.format('YYYY/MM/DD')
        );
      }

      if (searchGenre) {
        result = result.filter(
          (issue: Issue) => issue?.genre === Number(searchGenre)
        );
      }

      if (searchPurpose) {
        result = result.filter(
          (issue: Issue) =>
            issue.purpose && issue.purpose[0] === Number(searchPurpose)
        );
      }

      if (searchBudget) {
        result = result.filter(
          (issue: Issue) => issue?.budget === searchBudget
        );
      }

      if (searchMediaType) {
        result = result.filter(
          (issue: Issue) =>
            issue.mediaType && issue?.mediaType[0] === searchMediaType
        );
      }

      if (searchMenu) {
        result = result.filter(
          (issue: Issue) =>
            Array.isArray(issue?.menu) &&
            (issue?.menu as number[]).includes(searchMenu)
        );
      }
      // 重複を除外
      result = result.filter(
        (issue, index, self) =>
          index === self.findIndex((i) => i.issueId === issue.issueId)
      );
      return result;
    });
  };

  const loadMore = async (): Promise<void> => {
    if (currentUser === null) return;

    const { docs, empty } = await getIssueInfiniteScrollfromClient(
      issueList,
      currentUser
    );
    if (empty) {
      setHasMore(false);
      return;
    }
    appendIssues(docs);
  };

  useEffect(() => {
    void (async () => {
      try {
        setLoading(true);
        await loadMore();
      } catch (e) {
        throwError(e);
      } finally {
        setShowList(true);
        setLoading(false);
      }
    })();
  }, [currentUser]);

  const [companyStatus, setCompanyStatus] = React.useState<number>(2);

  useEffect(() => {
    // 会社のステータスを取得
    void (async () => {
      if (currentUser) {
        const userRef = doc(usersRef, currentUser.uid);
        const userSnap = await getDoc(userRef);
        const userData = userSnap.data();
        const companyId = userData?.companyId as string;
        if (companyId) {
          const companyRef = doc(companiesRef, companyId);
          const companiesSnap = await getDoc(companyRef);
          const companyData = companiesSnap.data();
          setCompanyStatus(companyData?.status as number);
        }
      }
    })();
  }, [currentUser?.uid]);

  return (
    <>
      <CustomLoading loading={loading} />
      <ClientAgentIssueSearchCard
        handleChange={handleSearchInputChange}
        screen={screenType.client}
      />
      <Typography component="div">
        {showList && (
          <InfiniteScroll
            loadMore={() => {
              void (async () => {
                await loadMore();
              })();
            }}
            hasMore={hasMore}
            loader={<div className="loader" key={0}></div>}
          >
            {!searchProductName &&
            !searchStartDate.start &&
            !searchStartDate.end &&
            !searchApplicationDate.start &&
            !searchApplicationDate.end &&
            !searchGenre &&
            !searchPurpose &&
            !searchBudget &&
            !searchMediaType &&
            !searchMenu &&
            searchedIssues.length === 0 ? (
              <NothingGivenList
                title={t('list.title')}
                detail={t('list.detail')}
              />
            ) : searchedIssues.length !== 0 ? (
              searchedIssues.map((issue: Issue) => (
                <Typography sx={styles.card} key={issue.issueId}>
                  <MypageIssueCard
                    issue={issue}
                    screen={screenType.client}
                    companyStatus={companyStatus}
                  />
                </Typography>
              ))
            ) : (
              <NothingGivenList
                title={t('list.searchTitle')}
                detail={t('list.searchDetail')}
              />
            )}
          </InfiniteScroll>
        )}
      </Typography>
    </>
  );
};
export default ClientIssueList;
